import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import { queryKeys } from './queryKeys';

const FIVE_MIN = 5 * 60 * 1000;

/**
 * List deployed agents (workflows) for a project, React-Query cached.
 * Backed by GET /projects/:projectId/workflows — minimal per-workflow
 * metadata (workflowType, uuid, version, updatedAt, displayName, iconUrl,
 * bundleStatus, warmEnabled). Replaces PipelinesPage's manual fetch loop.
 */
export const useAgents = (projectId, options = {}) => {
  return useQuery({
    queryKey: queryKeys.agents.list(projectId),
    queryFn: async () => {
      const data = await api.get(`/projects/${projectId}/workflows`);
      return Array.isArray(data) ? data : [];
    },
    enabled: !!projectId,
    staleTime: FIVE_MIN,
    ...options,
  });
};

/**
 * One agent's detail, React-Query cached. Backed by
 * GET /projects/:projectId/workflows/:workflowType — the live access pattern
 * the detail page uses (keyed by the immutable workflowType slug, NOT a uuid).
 *
 * Exported + ready for PipelineDetailPage to adopt later. It is NOT wired into
 * any page yet (PipelineDetailPage has another agent's uncommitted WIP and is
 * intentionally left untouched).
 */
export const useAgent = (projectId, workflowType, options = {}) => {
  return useQuery({
    queryKey: queryKeys.agents.detail(projectId, workflowType),
    queryFn: () => api.get(`/projects/${projectId}/workflows/${workflowType}`),
    enabled: !!projectId && !!workflowType,
    staleTime: FIVE_MIN,
    ...options,
  });
};

/**
 * Delete an agent, then keep the cache honest:
 *   - invalidate the project's agent LIST → the deleted row leaves the table
 *   - remove the agent's DETAIL entry     → no ghost detail served from cache
 *
 * Backend wires DELETE on /workflows/:uuid (looks the workflow up via the UUID
 * GSI, verifies project membership server-side). Pass
 * `{ uuid, workflowType }` to `mutate()` — `uuid` for the request, both used
 * to scope the cache cleanup.
 */
export const useDeleteAgent = (projectId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid }) => api.delete(`/workflows/${uuid}`),
    onSuccess: (_data, { workflowType }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.agents.list(projectId) });
      if (workflowType) {
        queryClient.removeQueries({ queryKey: queryKeys.agents.detail(projectId, workflowType) });
      }
      // Account-wide sidebar pinned list is its own query — refresh it so the
      // deleted agent drops out of the sidebar instantly (not just the
      // project's list table).
      queryClient.invalidateQueries({ queryKey: ['workspace', 'sidebar'] });
    },
  });
};
